<script setup lang="ts">
// Closed-beta access requests from the public landing form (admin triage).
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

import { api } from "@/api/client";
import type { BetaRequest, BetaRequestStatus } from "@/api/types";
import { describeError } from "@/utils/errors";

const { t } = useI18n();

const items = ref<BetaRequest[]>([]);
const notifyEnabled = ref(false);
const loading = ref(true);
const listError = ref<string | null>(null);
const statusFilter = ref<"" | BetaRequestStatus>("");

const STATUSES: BetaRequestStatus[] = ["new", "contacted", "accepted", "rejected"];

const filtered = computed(() =>
  statusFilter.value ? items.value.filter((r) => r.status === statusFilter.value) : items.value,
);
const newCount = computed(() => items.value.filter((r) => r.status === "new").length);

async function reload() {
  loading.value = true;
  listError.value = null;
  try {
    const r = await api.admin.betaRequests.list();
    items.value = r.beta_requests;
    notifyEnabled.value = r.notify_enabled;
  } catch (err) {
    listError.value = describeError(err, t);
  } finally {
    loading.value = false;
  }
}

async function setStatus(r: BetaRequest, status: BetaRequestStatus) {
  try {
    const updated = await api.admin.betaRequests.update(r.id, status);
    const i = items.value.findIndex((x) => x.id === r.id);
    if (i >= 0) items.value[i] = updated;
  } catch (err) {
    listError.value = describeError(err, t);
  }
}

function tone(status: BetaRequestStatus): string {
  return status === "accepted"
    ? "ok"
    : status === "rejected"
      ? "bad"
      : status === "contacted"
        ? "warn"
        : "info";
}

function when(iso: string): string {
  return iso.slice(0, 16).replace("T", " ");
}

onMounted(reload);
</script>

<template>
  <div class="admin-page">
    <div class="admin-page__header">
      <h1>
        {{ t("admin.beta.title") }}
        <span v-if="newCount" class="admin-badge" data-tone="info" data-test="beta-new-count">
          {{ t("admin.beta.new_count", { n: newCount }) }}
        </span>
      </h1>
      <label class="beta__filter">
        <span>{{ t("admin.beta.status") }}</span>
        <select v-model="statusFilter" data-test="beta-status-filter">
          <option value="">{{ t("admin.beta.all") }}</option>
          <option v-for="s in STATUSES" :key="s" :value="s">
            {{ t(`admin.beta.status_${s}`) }}
          </option>
        </select>
      </label>
    </div>

    <p class="admin-hint">
      {{ notifyEnabled ? t("admin.beta.notify_on") : t("admin.beta.notify_off") }}
    </p>

    <section class="admin-card">
      <p v-if="loading" class="admin-empty">{{ t("admin.loading") }}</p>
      <p v-else-if="listError" class="admin-error">{{ listError }}</p>
      <p v-else-if="filtered.length === 0" class="admin-empty">{{ t("admin.beta.empty") }}</p>
      <table v-else class="admin-table">
        <thead>
          <tr>
            <th>{{ t("admin.beta.col_when") }}</th>
            <th>{{ t("admin.beta.col_who") }}</th>
            <th>{{ t("admin.beta.col_purpose") }}</th>
            <th>{{ t("admin.beta.col_fleet") }}</th>
            <th>{{ t("admin.beta.status") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in filtered" :key="r.id" :data-test="`beta-row-${r.id}`">
            <td class="admin-table__id" :data-label="t('admin.beta.col_when')">
              {{ when(r.created_at) }}
            </td>
            <td :data-label="t('admin.beta.col_who')">
              <strong>{{ r.name }}</strong>
              <br />
              <a :href="`mailto:${r.email}`">{{ r.email }}</a>
            </td>
            <td :data-label="t('admin.beta.col_purpose')" class="beta__text">
              <div>{{ r.purpose || "—" }}</div>
              <div v-if="r.comment" class="beta__comment">{{ r.comment }}</div>
            </td>
            <td :data-label="t('admin.beta.col_fleet')">
              <div>{{ t("admin.beta.workstations") }}: {{ r.workstations ?? "—" }}</div>
              <div>{{ t("admin.beta.servers") }}: {{ r.servers ?? "—" }}</div>
              <div v-if="r.os">{{ t("admin.beta.os") }}: {{ r.os }}</div>
            </td>
            <td :data-label="t('admin.beta.status')">
              <span
                class="admin-badge"
                :data-tone="tone(r.status)"
                :data-test="`beta-status-${r.id}`"
              >
                {{ t(`admin.beta.status_${r.status}`) }}
              </span>
              <select
                :value="r.status"
                :data-test="`beta-set-${r.id}`"
                @change="
                  setStatus(r, ($event.target as HTMLSelectElement).value as BetaRequestStatus)
                "
              >
                <option v-for="s in STATUSES" :key="s" :value="s">
                  {{ t(`admin.beta.status_${s}`) }}
                </option>
              </select>
            </td>
          </tr>
        </tbody>
      </table>
    </section>
  </div>
</template>

<style scoped>
.beta__filter {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.85rem;
}
.beta__text {
  max-width: 28rem;
  white-space: pre-wrap;
}
.beta__comment {
  margin-top: 0.35rem;
  color: var(--color-fg-dim);
}
.admin-hint {
  margin: 0 0 0.75rem;
  font-size: 0.85rem;
  color: var(--color-fg-dim);
}
td select {
  margin-left: 0.5rem;
}
</style>
